import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../utils/apiConfig';
import { buildLogoUrl } from '../utils/playerCardUtils';
import './GroupingSummary.css';

function GroupingSummary({ tournamentCode, teams = [] }) {
  const [groupingData, setGroupingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedGroups, setExpandedGroups] = useState(new Set());

  useEffect(() => {
    const fetchGroupingData = async () => {
      const token = localStorage.getItem('token');
      const isPublicPage = window.location.pathname.includes('/live/');
      
      console.log('[GroupingSummary] Fetching data:', { tournamentCode, isPublicPage, hasToken: !!token, teamsCount: teams?.length });
      
      // For non-public pages without token, skip
      if (!token && !isPublicPage) {
        console.log('[GroupingSummary] Skipping - not public page and no token');
        setGroupingData(null);
        setLoading(false);
        return;
      }

      // Try to fetch from API first
      try {
        // For public pages, use the public endpoint
        // For authenticated pages, use the authenticated endpoint
        const endpoint = isPublicPage && !token
          ? `${API_BASE_URL}/api/grouping/${tournamentCode}/groups/public`
          : `${API_BASE_URL}/api/grouping/${tournamentCode}/groups`;

        console.log('[GroupingSummary] Calling endpoint:', endpoint);

        // Prepare headers - include token if available
        const headers = {};
        if (token) {
          headers.Authorization = `Bearer ${token}`;
        }

        const response = await axios.get(endpoint, { headers });

        console.log('[GroupingSummary] API response:', { 
          success: response.data.success, 
          groupsCount: response.data.groups?.length 
        });

        if (response.data.success && response.data.groups && response.data.groups.length > 0) {
          setGroupingData(response.data);
          setLoading(false);
          return;
        }
      } catch (error) {
        console.log('[GroupingSummary] API error:', { 
          status: error.response?.status, 
          message: error.message,
          isPublicPage 
        });
        
        // For public pages, if API fails, try to construct from teams prop
        if (isPublicPage) {
          // Don't log 401/403 errors for public pages - it's expected
          if (error.response && (error.response.status === 401 || error.response.status === 403)) {
            console.log('[GroupingSummary] Auth error on public page, trying fallback');
            // Silently continue to fallback
          } else {
            console.error('[GroupingSummary] Error fetching grouping data:', error);
          }
        } else {
          // For non-public pages, let the error propagate (axios interceptor will handle redirect)
          if (error.response && (error.response.status === 401 || error.response.status === 403)) {
            throw error;
          }
          console.error('[GroupingSummary] Error fetching grouping data:', error);
        }
      }

      // Fallback: Try to construct grouping data from teams prop
      console.log('[GroupingSummary] Trying fallback from teams prop:', { teamsCount: teams?.length });
      if (teams && teams.length > 0) {
        // Check teams structure
        console.log('[GroupingSummary] Sample team data:', teams[0]);
        
        // Group teams by their group property
        const groupedTeams = {};
        teams.forEach(team => {
          // Check if team has group property
          const teamGroup = team.group;
          if (teamGroup && String(teamGroup).trim() !== '') {
            const groupKey = String(teamGroup).trim();
            if (!groupedTeams[groupKey]) {
              groupedTeams[groupKey] = [];
            }
            groupedTeams[groupKey].push(team);
          }
        });

        console.log('[GroupingSummary] Grouped teams:', Object.keys(groupedTeams), 'groups found');

        // Convert to the expected format
        const groups = Object.keys(groupedTeams).sort().map(groupName => ({
          name: groupName,
          teams: groupedTeams[groupName]
        }));

        if (groups.length > 0) {
          console.log('[GroupingSummary] Setting grouping data from teams fallback:', groups.length, 'groups');
          setGroupingData({
            success: true,
            groups: groups
          });
          setLoading(false);
          return;
        } else {
          console.log('[GroupingSummary] No teams have group property assigned');
        }
      }

      // If no grouping data available from either source
      console.log('[GroupingSummary] No grouping data available');
      setGroupingData(null);
      setLoading(false);
    };

    if (tournamentCode) {
      fetchGroupingData();
    } else {
      setLoading(false);
    }
  }, [tournamentCode, teams]);

  const toggleGroup = (groupName) => {
    setExpandedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(groupName)) {
        newSet.delete(groupName);
      } else {
        newSet.add(groupName);
      }
      return newSet;
    });
  };

  if (loading) {
    return (
      <div className="grouping-summary-container">
        <div className="grouping-summary-loading">Loading grouping data...</div>
      </div>
    );
  }

  // Determine the grouping data to use - from API or constructed from teams
  let displayData = groupingData;
  
  // If no grouping data from API, try to construct from teams prop as fallback
  if (!displayData || !displayData.groups || displayData.groups.length === 0) {
    console.log('[GroupingSummary] No API data, trying to construct from teams');
    // Try to construct grouping data from teams prop
    if (teams && teams.length > 0) {
      // Group teams by their group property
      const groupedTeams = {};
      teams.forEach(team => {
        if (team.group && team.group.trim() !== '') {
          if (!groupedTeams[team.group]) {
            groupedTeams[team.group] = [];
          }
          groupedTeams[team.group].push(team);
        }
      });

      // Convert to the expected format
      const groups = Object.keys(groupedTeams).sort().map(groupName => ({
        name: groupName,
        teams: groupedTeams[groupName]
      }));

      if (groups.length > 0) {
        console.log('[GroupingSummary] Using constructed data:', groups.length, 'groups');
        displayData = {
          success: true,
          groups: groups
        };
      }
    }
  }

  // If still no data, don't render
  if (!displayData || !displayData.groups || displayData.groups.length === 0) {
    console.log('[GroupingSummary] No grouping data available, returning null');
    return null;
  }

  // Create a map of team IDs to team data for quick lookup
  const teamsMap = new Map();
  teams.forEach(team => {
    teamsMap.set(team._id?.toString(), team);
  });

  const totalTeamsInGroups = displayData.groups.reduce((sum, group) => {
    return sum + (group.teams?.length || 0);
  }, 0);

  console.log('[GroupingSummary] Rendering with data:', { 
    groupsCount: displayData.groups.length, 
    totalTeams: totalTeamsInGroups 
  });

  return (
    <div className="grouping-summary-container">
      <div className="grouping-summary-header">
        <h3 className="grouping-summary-title">🎯 Team Grouping Summary</h3>
        <div className="grouping-summary-stats">
          <span className="grouping-stat-item">
            <strong>{displayData.groups.length}</strong> Groups
          </span>
          <span className="grouping-stat-item">
            <strong>{totalTeamsInGroups}</strong> Teams Assigned
          </span>
        </div>
      </div>

      <div className="grouping-summary-content">
        {displayData.groups.map((group) => {
          const isExpanded = expandedGroups.has(group.name);
          const groupTeams = group.teams || [];
          
          // Handle both API format (team IDs) and constructed format (team objects)
          const groupTeamsData = groupTeams
            .map(team => {
              // If team is already an object with _id, use it directly
              if (typeof team === 'object' && team._id) {
                return team;
              }
              // Otherwise, try to look it up from teamsMap
              const teamIdStr = team?._id?.toString() || team?.toString();
              return teamsMap.get(teamIdStr) || team;
            })
            .filter(Boolean);

          return (
            <div key={group.name} className="grouping-group-card">
              <div 
                className="grouping-group-header"
                onClick={() => toggleGroup(group.name)}
              >
                <div className="grouping-group-title">
                  <span className="grouping-group-name">Group {group.name}</span>
                  <span className="grouping-group-count">
                    {groupTeamsData.length} {groupTeamsData.length === 1 ? 'Team' : 'Teams'}
                  </span>
                </div>
                <div className="grouping-group-toggle">
                  {isExpanded ? '▼' : '▶'}
                </div>
              </div>

              {isExpanded && (
                <div className="grouping-group-teams">
                  {groupTeamsData.length > 0 ? (
                    <div className="grouping-teams-grid">
                      {groupTeamsData.map((team, index) => {
                        const teamData = typeof team === 'object' && team._id ? team : teamsMap.get(team?._id?.toString() || team?.toString());
                        if (!teamData) return null;

                        return (
                          <div key={teamData._id || index} className="grouping-team-item">
                            <div className="grouping-team-logo">
                              {teamData.logo ? (
                                <img 
                                  src={buildLogoUrl(teamData.logo)} 
                                  alt={teamData.name}
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    if (e.target.nextSibling) {
                                      e.target.nextSibling.style.display = 'flex';
                                    }
                                  }}
                                />
                              ) : null}
                              <div 
                                className="grouping-team-logo-placeholder"
                                style={{ display: teamData.logo ? 'none' : 'flex' }}
                              >
                                {teamData.name?.charAt(0) || '?'}
                              </div>
                            </div>
                            <div className="grouping-team-info">
                              <div className="grouping-team-name">{teamData.name}</div>
                              {teamData.city && (
                                <div className="grouping-team-city">📍 {teamData.city}</div>
                              )}
                              {teamData.playersBought !== undefined && (
                                <div className="grouping-team-stats">
                                  <span>{teamData.playersBought || 0} Players</span>
                                  {teamData.spent !== undefined && (
                                    <span>₹{teamData.spent?.toLocaleString() || '0'}</span>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="grouping-empty-group">No teams assigned yet</div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default GroupingSummary;

